import { useEffect, useState } from "react";

interface TurnTimerProps {
  initialSeconds: number;
  isPaused?: boolean;
  onExpire?: () => void;
}

export default function TurnTimer({ initialSeconds, isPaused = false, onExpire }: TurnTimerProps) {
  const [flashState, setFlashState] = useState<boolean>(false);
  const secondsRemaining = initialSeconds;
  const isExpired = secondsRemaining <= 0;

  // Flash effect for last 30 seconds (only when not paused)
  useEffect(() => {
    if (isPaused || secondsRemaining > 30 || secondsRemaining <= 0) {
      setFlashState(false);
      return;
    }

    const flashInterval = setInterval(() => {
      setFlashState((prev) => !prev);
    }, 500); // Flash every 500ms (2 times per second)ß
    return () => clearInterval(flashInterval);
  }, [secondsRemaining, isPaused]);

  // Call onExpire when timer reaches 0
  useEffect(() => {
    if (isExpired && onExpire) {
      onExpire();
    }
  }, [isExpired, onExpire]);

  // Format seconds to MM:SS
  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Determine color based on time remaining
  const getColor = (): string => {
    if (isPaused || isExpired || secondsRemaining === 0) {
      return "#6c757d"; // Gray when paused or expired
    }
    if (secondsRemaining <= 30) {
      // Flash every 250ms in last 30 seconds with red colors
      return flashState ? "#dc3545" : "#ff1744"; // Alternating red shades for more significant flashing
    }
    if (secondsRemaining <= 60) {
      return "#ff9800"; // Orange in last minute
    }
    return "#007bff"; // Blue normally
  };

  const displayTime = formatTime(secondsRemaining);
  const color = getColor();

  return (
    <div
      style={{
        padding: "0.5rem 1rem",
        backgroundColor: color,
        color: "white",
        borderRadius: "8px",
        fontSize: "1.25rem",
        fontWeight: "bold",
        fontFamily: "monospace",
        minWidth: "80px",
        height: "2.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        boxSizing: "border-box",
        margin: 0,
        transition: secondsRemaining <= 30 && secondsRemaining > 0 && !isPaused 
          ? "background-color 0.1s ease" 
          : "background-color 0.3s ease",
        boxShadow: secondsRemaining <= 30 && secondsRemaining > 0 && !isPaused && flashState
          ? "0 0 20px rgba(220, 53, 69, 0.8)" 
          : "none",
      }}
    >
      {displayTime}
    </div>
  );
}

